import { Duration } from 'luxon'

/**
 * Formate un numéro de téléphone au format XX.XX.XX.XX.XX
 * @param {string} number - Le numéro de téléphone à formater
 * @returns {string} Le numéro formaté ou le numéro original si le format ne correspond pas
 */
export const formatPhoneNumber = (number) => {
  if (!number) return ''
  
  // Supprimer tous les caractères non numériques
  const cleanNumber = number.replace(/\D/g, '')
  
  // Si le numéro n'a pas 10 chiffres (format français standard), on le retourne tel quel
  if (cleanNumber.length !== 10) {
    return number
  }
  
  // Formater en XX XX XX XX XX
  return cleanNumber.replace(/(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})/, '$1 $2 $3 $4 $5')
}

/**
 * Formate un numéro SIREN (9 chiffres) ou SIRET (14 chiffres)
 * @param {string} number - Le numéro à formater
 * @returns {string} Le numéro formaté ou le numéro original si le format ne correspond pas
 */
export const formatSiret = (number) => {
  if (!number) return ''
  
  // Supprimer tous les caractères non numériques
  const cleanNumber = number.replace(/\D/g, '')
  
  // Formater selon la longueur du numéro
  if (cleanNumber.length === 9) {
    // SIREN (9 chiffres) - Format XXX XXX XXX
    return cleanNumber.replace(/(\d{3})(\d{3})(\d{3})/, '$1 $2 $3')
  } else if (cleanNumber.length === 14) {
    // SIRET (14 chiffres) - Format XXX XXX XXX XXXXX
    return cleanNumber.replace(/(\d{3})(\d{3})(\d{3})(\d{5})/, '$1 $2 $3 $4')
  } else {
    // Si le numéro n'a ni 9 ni 14 chiffres, on le retourne tel quel
    return number
  }
}

export const formatSurface = (surface) => surface ? `${formatNumber(surface)} m²` : ''
export const formatSurfaceAre = (surface) => {
  // Convertir la surface en m² en hectares, ares et centiares
  // 1 hectare = 10000 m²
  const ha = Math.floor(surface/ 10000)
  
  // 1 are = 100 m²
  const a = Math.floor((surface % 10000) / 100)
  
  // 1 centiare = 1 m²
  const ca = surface % 100

  return `${ha} ha ${a.toString().length > 1 ? a : `0${a}`} a ${ca.toString().length > 1 ? ca : `0${ca}`} ca`
}

export const formatDate = (date) => date ? new Date(date).toLocaleDateString() : ''
export const formatDateTime = (datetime) => datetime ? new Date(datetime).toLocaleString('fr-FR') : ''

export const formatNumber = (number) => number ? new Intl.NumberFormat('fr-FR').format(number) : ''

export const formatPrice = (number, options = {}) => {
  if(!number && number !== 0) return ''
  const {currency = 'EUR', digits = 2, locale = 'fr-FR'} = options 
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: digits,
    maximumFractionDigits: digits
  }).format(number)
}

/**
 * Formate un nombre de jours en jours, semaines, mois ou années selon la quantité
 * en utilisant la bibliothèque Luxon et l'API Intl pour la localisation
 * @param {number} days - Le nombre de jours à formater
 * @param {Object} options - Options de formatage
 * @param {boolean} options.short - Utiliser le format court au lieu du format long
 * @param {string} options.locale - La locale à utiliser (par défaut: 'fr-FR')
 * @returns {string} Le nombre formaté avec l'unité appropriée ou chaîne vide si invalide
 */
export const formatDays = (days, options = {}) => {
  if (!days && days !== 0) return ''
  
  const daysNum = Number(days)
  
  // Vérifier que la valeur est un nombre valide
  if (isNaN(daysNum)) return ''
  
  const {
    short = false,
    locale = 'fr-FR'
  } = options
  
  // Créer une durée Luxon à partir du nombre de jours
  const duration = Duration.fromObject({ days: daysNum })
  
  // Déterminer l'unité la plus appropriée et sa valeur
  let value, unit
  
  if (daysNum >= 365) {
    value = Math.floor(duration.as('years'))
    unit = 'year'
  } else if (daysNum >= 30) {
    value = Math.floor(duration.as('months'))
    unit = 'month'
  } else {
    value = daysNum
    unit = 'day'
  }
  
  // Formatter le nombre selon la locale
  const numberFormatter = new Intl.NumberFormat(locale)
  const formattedValue = numberFormatter.format(value)
  
  // Définir les unités localisées
  const unitLabels = {
    'fr-FR': {
      year: {
        long: value => value === 1 ? 'an' : 'ans',
        short: () => 'a'
      },
      month: {
        long: () => 'mois',
        short: () => 'm'
      },
      week: {
        long: value => value === 1 ? 'semaine' : 'semaines',
        short: () => 'sem'
      },
      day: {
        long: value => value === 1 ? 'jour' : 'jours',
        short: () => 'j'
      }
    },
    'en-US': {
      year: {
        long: value => value === 1 ? 'year' : 'years',
        short: () => 'yr'
      },
      month: {
        long: value => value === 1 ? 'month' : 'months',
        short: () => 'mo'
      },
      week: {
        long: value => value === 1 ? 'week' : 'weeks',
        short: () => 'wk'
      },
      day: {
        long: value => value === 1 ? 'day' : 'days',
        short: () => 'd'
      }
    }
  }
  
  // Utiliser la locale spécifiée ou une locale de secours
  const localeLabels = unitLabels[locale] || unitLabels['fr-FR']
  const format = short ? 'short' : 'long'
  const unitLabel = localeLabels[unit][format](value)
  
  return `${formattedValue} ${unitLabel}`
}

// Formater la date (mois et année)
export const formatPeriod = (year, month) => {
  const date = new Date(year, month - 1)
  return date.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })
}